import { FormLabel } from '@components/shared/form-label.component.tsx';
import { Tile } from '@components/shared/tile.component';
import { Input } from '@components/ui/input';
import { errorToast, successToast } from '@hooks/toast.hook.tsx';
import { cn } from '@lib/utils';
import { useGetCurrentUser } from '@queries/users.query';
import { referralStore } from '@stores/referral.store';
import { XIcon } from 'lucide-react';
import React from 'react';
import { useStore } from 'zustand';

export const ReferralTile: React.FC = () => {
  const { data: user } = useGetCurrentUser();
  const { hasDismissedReferralCode, dismissReferralCode } = useStore(referralStore);

  const referralLink = user?.profile?.referralCode
    ? `${window.location.origin}/?referrerCode=${encodeURIComponent(user.profile.referralCode)}`
    : null;

  const handleDismiss = () => {
    if (!user?.profile?.referralCode) return;
    dismissReferralCode(user.profile.referralCode);
  };

  const handleCopy = async () => {
    if (!referralLink) return;

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(referralLink);
        successToast({ description: 'Referral link copied to clipboard' });
      } else {
        errorToast({ description: 'Clipboard not supported in this browser.' });
      }
    } catch (error) {
      errorToast({ description: 'Error copying referral link.' });
    }
  };

  const isDismissed = user?.profile?.referralCode && hasDismissedReferralCode(user.profile.referralCode);
  const userHasValidAccess = Boolean(user?.hasValidAccess);

  if (!referralLink || !userHasValidAccess || isDismissed) {
    return null;
  }

  return (
    <Tile
      className="relative"
      header={{
        title: 'Tyr Referrals',
        iconButton: {
          icon: <XIcon />,
          onClick: handleDismiss,
        },
      }}
      hoverReveal
    >
      <section className="flex justify-between items-start gap-2">
        <div className="flex-1 space-y-2">
          <FormLabel
            label="Use this link to refer users to Tyr:"
            htmlFor="referral-link"
          />
          <Input
            className={cn('cursor-pointer', 'hover:bg-gray-50 focus:bg-gray-50', 'transition-colors duration-200')}
            id="referral-link"
            onClick={handleCopy}
            readOnly
            title="Copy referral link"
            value={referralLink}
          />
        </div>
      </section>
    </Tile>
  );
};
